import { useEffect, useState, useCallback, memo } from 'react';
import {
    List,
    ListItemText,
    Collapse,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    ListItemButton
} from '@mui/material';
import { useSearchParams } from "react-router-dom";

import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { theme } from '../../../../theme';
import { fetchSkillsWithSubSkills, updateStoredJobDetails } from '../../service';
import HeaderTexts from '../../common/HeaderTexts';

// ------------------- SubSkillList -------------------
const SubSkillList = memo(({ selectSubSkill, subSkills, onSubSkillSelect }) => {
    const [searchParams] = useSearchParams();
    const retryJob = Boolean(Number(searchParams.get("retryJob")));

    const handleClick = useCallback((subSkill) => {
        if (!retryJob) {
            onSubSkillSelect(subSkill);
            updateStoredJobDetails("subSkill", subSkill);
        };
    }, [retryJob, onSubSkillSelect]);

    return (
        <List
            component="div"
            disablePadding
            sx={{
                backgroundColor: '#E8FAEB',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
                overflow: 'hidden'
            }}
        >
            <FormControl component="fieldset" fullWidth>
                {subSkills.map((subSkill, index) => {
                    const isSelected =
                        selectSubSkill?.sub_skill_id === subSkill.sub_skill_id;

                    return (
                        <FormControlLabel
                            key={subSkill.sub_skill_id}
                            value={subSkill.sub_skill_id}
                            labelPlacement="start"
                            onClick={() => handleClick(subSkill)}
                            control={
                                <Radio
                                    size="small"
                                    checked={isSelected}
                                    disabled={retryJob}
                                    sx={{
                                        color: '#9E9E9E',
                                        '&.Mui-checked': {
                                            color: theme.main_code,
                                        },
                                    }}
                                />
                            }
                            label={subSkill.sub_skill_name}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                margin: 0,
                                paddingBottom: index === subSkills.length - 1
                                        ? '15px'
                                        : '5px',
                                paddingLeft: '40px',
                                paddingRight: '10px',
                                cursor: retryJob ? 'not-allowed' : 'pointer',

                                '& .MuiFormControlLabel-label': {
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? '#000000' : '#6B7280',
                                    // fontSize: stepFlowIsEnable ? '15px' : '',
                                }
                            }}
                        />
                    );
                })}
            </FormControl>
        </List>
    );
});

// ------------------- SkillItem -------------------
const SkillItem = memo(({
    skill,
    selectSkill,
    selectSubSkill,
    onSkillSelect,
    onSubSkillSelect
}) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOpen(selectSkill?.skill_id === skill.skill_id);
    }, [selectSkill?.skill_id, skill.skill_id]);

    const handleClick = useCallback(() => {
        setOpen(prev => !prev);

        const selectedSkill = {
            skill_id: skill.skill_id,
            skill_name: skill.skill_name,
        }

        onSkillSelect(selectedSkill);
        updateStoredJobDetails("skill", selectedSkill);
    }, [skill.skill_id, skill.skill_name, onSkillSelect]);

    return (
        <>
            <ListItemButton
                onClick={handleClick}
                sx={{
                    ...(open && {
                        backgroundColor: '#E8FAEB',
                        borderTopLeftRadius: '12px',
                        borderTopRightRadius: "12px",
                        "&:hover": {
                            backgroundColor: "#E8FAEB",
                        },
                    }),
                }}
            >
                <ListItemText
                    primary={
                        <>
                            <i 
                                className={`material-symbols-rounded opacity-10 text-${open ? theme.main : 'dark'} me-2`}
                                // style={{fontSize: stepFlowIsEnable ? '18px' : ''}}
                            >
                                {skill.skill_web_app_icon}
                            </i>
                            <span 
                                className={`${open && 'text-bold text-dark'}`} 
                                // style={{fontSize: stepFlowIsEnable ? '15px' : ''}}
                            >
                                {skill.skill_name}
                            </span>
                        </>
                    }
                />
                {open ? <ExpandMore sx={{color: '#09b629d9'}}/> : <ChevronRightIcon />}
            </ListItemButton>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <SubSkillList
                    selectSubSkill={selectSubSkill}
                    subSkills={skill.sub_skills}
                    onSubSkillSelect={onSubSkillSelect}
                />
            </Collapse>

            <Divider />
        </>
    );
});

// ------------------- Skills -------------------
const Skills = ({
    selectSkill,
    setSkill,
    selectSubSkill,
    setSubSkill,
    isHome = false,
    handleReset = null
}) => {
    const [skills, setSkills] = useState({ loading: true, list: [] });

    useEffect(() => {
        let active = true;

        const fetchData = async () => {
            try {
                const response = await fetchSkillsWithSubSkills();
                if (active) {
                    setSkills({
                        loading: false,
                        list: response?.list || []
                    });
                }
            } catch (error) {
                if (active) setSkills({ loading: false, list: [] });
            }
        };

        fetchData();

        return () => { active = false };
    }, []);

    const handleSkill = useCallback(setSkill, [setSkill]);
    const handleSubSkill = useCallback(setSubSkill, [setSubSkill]);

    const containerHeight = isHome
        ? selectSkill
            ? '560px'
            : '380px'
        : undefined;

    return (
        <div className="mx-3">
            <HeaderTexts title="What do you need help with?" subTitle="Choose a service to get started." onButtonClick={handleReset}/>

            <div
                className={`${!isHome && 'mb-4'}`}
                style={{
                    overflow: 'auto',
                    height: containerHeight
                }}
            >
                <List sx={{ width: '100%', bgcolor: 'background.paper' }} component="nav">
                    <RadioGroup name="sub-skills-group">
                        {skills.list.map((skill) =>
                            skill.sub_skills.length > 0 && (
                                <SkillItem
                                    key={skill.skill_id}
                                    skill={skill}
                                    selectSkill={selectSkill}
                                    selectSubSkill={selectSubSkill}
                                    onSkillSelect={handleSkill}
                                    onSubSkillSelect={handleSubSkill}
                                />
                            )
                        )}
                    </RadioGroup>
                </List>
            </div>
        </div>
    );
};

export default Skills;